import { useState } from "react";
import { operationLogs, type OperationLog } from "@/mocks/settings";

const moduleColors: Record<string, string> = {
  "系统设置": "bg-purple-100 text-purple-700",
  "告警中心": "bg-red-100 text-red-700",
  "报表分析": "bg-blue-100 text-blue-700",
  "在线售气": "bg-green-100 text-green-700",
  "档案管理": "bg-orange-100 text-orange-700",
  "用户认证": "bg-gray-100 text-gray-700",
  "用户权限": "bg-yellow-100 text-yellow-700",
  "气价管理": "bg-teal-100 text-teal-700",
  "支付充值": "bg-emerald-100 text-emerald-700",
};

export default function OperationLogs() {
  const [logs] = useState<OperationLog[]>(operationLogs);
  const [searchText, setSearchText] = useState("");
  const [filterModule, setFilterModule] = useState("全部");
  const [filterResult, setFilterResult] = useState("全部");
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const modules = ["全部", ...Array.from(new Set(logs.map(l => l.module)))];

  const filtered = logs.filter(l => {
    const matchSearch = !searchText || l.realName.includes(searchText) || l.action.includes(searchText) || l.detail.includes(searchText);
    const matchModule = filterModule === "全部" || l.module === filterModule;
    const matchResult = filterResult === "全部" || l.result === filterResult;
    return matchSearch && matchModule && matchResult;
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-4">
      {/* 统计卡片 */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "今日操作", value: logs.filter(l => l.time.startsWith("2026-04-10")).length, icon: "ri-calendar-check-line", color: "text-[#E8531A]", bg: "bg-orange-50" },
          { label: "成功操作", value: logs.filter(l => l.result === "成功").length, icon: "ri-checkbox-circle-line", color: "text-green-600", bg: "bg-green-50" },
          { label: "失败操作", value: logs.filter(l => l.result === "失败").length, icon: "ri-close-circle-line", color: "text-red-500", bg: "bg-red-50" },
          { label: "活跃用户", value: new Set(logs.map(l => l.userId)).size, icon: "ri-user-line", color: "text-blue-600", bg: "bg-blue-50" },
        ].map((item, i) => (
          <div key={i} className={`${item.bg} rounded-xl p-4 flex items-center gap-3`}>
            <div className="w-10 h-10 flex items-center justify-center">
              <i className={`${item.icon} text-2xl ${item.color}`}></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">{item.value}</div>
              <div className="text-xs text-gray-500">{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 筛选栏 */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm text-gray-400"></i>
          </div>
          <input
            value={searchText}
            onChange={e => { setSearchText(e.target.value); setCurrentPage(1); }}
            placeholder="搜索操作人/操作内容"
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 w-56"
          />
        </div>
        <select
          value={filterModule}
          onChange={e => { setFilterModule(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 cursor-pointer"
        >
          {modules.map(m => <option key={m}>{m}</option>)}
        </select>
        <select
          value={filterResult}
          onChange={e => { setFilterResult(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 cursor-pointer"
        >
          {["全部", "成功", "失败"].map(r => <option key={r}>{r}</option>)}
        </select>
        <span className="text-xs text-gray-400 ml-auto">共 {filtered.length} 条记录</span>
        <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-sm text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
          <i className="ri-download-line text-sm"></i> 导出日志
        </button>
      </div>

      {/* 日志列表 */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {["操作时间", "操作人", "模块", "操作内容", "IP地址", "结果", "详情"].map(h => (
                <th key={h} className="text-left text-xs text-gray-500 font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map(log => (
              <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{log.time}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#E8531A]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[#E8531A]">{log.realName[0]}</span>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-700">{log.realName}</div>
                      <div className="text-xs text-gray-400">@{log.userName}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${moduleColors[log.module] || "bg-gray-100 text-gray-600"}`}>
                    {log.module}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px]">
                  <div className="truncate">{log.action}</div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ip}</td>
                <td className="px-4 py-3">
                  <span className={`flex items-center gap-1 text-xs ${log.result === "成功" ? "text-green-600" : "text-red-500"}`}>
                    <i className={`${log.result === "成功" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}`}></i>
                    {log.result}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setSelectedLog(log)}
                    className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
                  >
                    查看
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* 分页 */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">第 {currentPage} / {totalPages} 页</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-7 h-7 flex items-center justify-center border rounded-lg text-xs cursor-pointer ${p === currentPage ? "bg-[#E8531A] text-white border-[#E8531A]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>

      {/* 日志详情弹窗 */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl p-6 w-[480px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">操作日志详情</h3>
              <button onClick={() => setSelectedLog(null)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              {[
                { label: "日志ID", value: selectedLog.id },
                { label: "操作时间", value: selectedLog.time },
                { label: "操作人", value: `${selectedLog.realName} (@${selectedLog.userName})` },
                { label: "所属模块", value: selectedLog.module },
                { label: "操作类型", value: selectedLog.action },
                { label: "操作详情", value: selectedLog.detail },
                { label: "IP地址", value: selectedLog.ip },
                { label: "设备信息", value: selectedLog.device },
                { label: "操作结果", value: selectedLog.result },
              ].map((item, i) => (
                <div key={i} className="flex gap-3">
                  <span className="text-sm text-gray-400 w-20 flex-shrink-0">{item.label}</span>
                  <span className={`text-sm flex-1 ${item.label === "操作结果" ? (selectedLog.result === "成功" ? "text-green-600 font-medium" : "text-red-500 font-medium") : "text-gray-700"}`}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
